import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Loader2, Clock, Pencil, Check } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChatSideDrawer } from "@/components/ChatSideDrawer";
import { ChatSenderAvatar, ChatSenderLabel } from "@/components/ChatSenderLabel";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";
import { ADMIN_ROLES } from "@/lib/roles";

interface Message {
  id: string;
  interest_id: string;
  sender_id: string;
  sender_role: string;
  sender_name: string | null;
  sender_avatar_url: string | null;
  body: string;
  is_read_by_buyer: boolean;
  is_read_by_admin: boolean;
  created_at: string;
  edited_at: string | null;
}

interface InterestChatProps {
  interestId: string;
  listingTitle?: string;
}

// Parse a server timestamp robustly. Safari/iOS rejects the space-separated
// form (e.g. "2026-06-16 21:15:00+00:00"), and a value with no offset must be
// read as UTC, not the viewer's local time — otherwise displayed times shift.
function parseDate(iso: string): Date {
  let s = iso.trim().replace(" ", "T");
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += "Z";
  return new Date(s);
}

function formatTime(iso: string) {
  return parseDate(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatFullDate(iso: string) {
  return parseDate(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function formatDateLabel(iso: string) {
  const d = parseDate(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
}

export function InterestChat({ interestId, listingTitle }: InterestChatProps) {
  const { user, hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  const isStaff = hasRole(...ADMIN_ROLES) || hasRole("bl_advisor");
  const closeChat = useCallback(() => setOpen(false), []);

  const fetchMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const msgs = await api.get<Message[]>(`/interests/${interestId}/messages`);
      setMessages(msgs);
      setUnread(0);
    } catch { /* silent */ }
    finally { if (!silent) setLoading(false); }
  };

  const fetchUnread = async () => {
    if (open) return;
    try {
      const res = await api.get<{ unread: number }>(`/interests/${interestId}/messages/unread-count`);
      setUnread(res.unread);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchUnread();
    const iv = setInterval(open ? () => fetchMessages(true) : fetchUnread, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, interestId]);

  useEffect(() => {
    if (open) {
      fetchMessages();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && messages.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  // Allow external buttons (e.g. sidebar "Chat") to open this panel.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-interest-chat", handler);
    return () => window.removeEventListener("open-interest-chat", handler);
  }, []);

  const handleSend = async () => {
    if (!body.trim() || sending) return;
    setSending(true);
    setSendError(null);
    try {
      const msg = await api.post<Message>(`/interests/${interestId}/messages`, { body: body.trim() });
      setMessages((prev) => [...prev, msg]);
      setBody("");
      // Buyer replies count against the daily allowance — nudge the advisor card's
      // "Messages today" meter to refetch so it reflects the spend immediately.
      if (!isStaff) window.dispatchEvent(new Event("outreach-used"));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      // Surface the daily-limit 429 (and any other failure) instead of dropping it.
      setSendError(err instanceof ApiError ? err.message : "Couldn't send. Please try again.");
      if (err instanceof ApiError && err.status === 429) window.dispatchEvent(new Event("outreach-used"));
    }
    finally { setSending(false); }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const startEdit = (m: Message) => {
    setEditingId(m.id);
    setEditBody(m.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };

  const handleEditSave = async (m: Message) => {
    const next = editBody.trim();
    if (!next || savingEdit) return;
    if (next === m.body) { cancelEdit(); return; }
    setSavingEdit(true);
    try {
      const updated = await api.patch<Message>(`/interests/${interestId}/messages/${m.id}`, { body: next });
      setMessages((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
      cancelEdit();
    } catch { /* silent */ }
    finally { setSavingEdit(false); }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, m: Message) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleEditSave(m); }
    if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
  };

  // Group by date
  const grouped: { dateLabel: string; msgs: Message[] }[] = [];
  for (const m of messages) {
    const label = formatDateLabel(m.created_at);
    const last = grouped[grouped.length - 1];
    if (last?.dateLabel === label) last.msgs.push(m);
    else grouped.push({ dateLabel: label, msgs: [m] });
  }

  const isMine = (m: Message) => m.sender_id === user?.id;

  return (
    <>
      {/* ── Chat rail — the same right-edge drawer as Niva ────────────── */}
      {open && (
        <ChatSideDrawer
          title="BL Advisor Chat"
          onClose={closeChat}
          subtitle={
            <>
              <span className="font-semibold text-primary">Niva</span> · AI Deal Assistant
              {listingTitle && <> · {listingTitle}</>}
            </>
          }
        >

          {/* Messages area */}
          <div
            ref={messagesRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-5 bg-muted"
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
                <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center">
                  <MessageCircle className="h-6 w-6 text-slate-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-600">No messages yet</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {isStaff ? "Send the buyer a message to start the conversation." : "Send a message to your BL Advisor below."}
                  </p>
                </div>
              </div>
            ) : (
              grouped.map((group) => (
                <div key={group.dateLabel} className="space-y-3">

                  {/* ── Date separator ── */}
                  <div className="flex items-center gap-2 py-1">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100">
                      {group.dateLabel}
                    </span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  {group.msgs.map((m) => {
                    const mine = isMine(m);
                    return (
                      <div
                        key={m.id}
                        className={`flex gap-2 ${mine ? "flex-row-reverse" : "flex-row"}`}
                        onMouseEnter={() => setHoveredId(m.id)}
                        onMouseLeave={() => setHoveredId(null)}
                      >
                        {/* Avatar */}
                        <ChatSenderAvatar role={m.sender_role} name={m.sender_name} src={m.sender_avatar_url} className="h-8 w-8 mt-auto" />

                        <div className={`flex min-w-0 flex-col max-w-[75%] ${mine ? "items-end" : "items-start"}`}>
                          {/* Sender name (for non-mine) */}
                          <ChatSenderLabel role={m.sender_role} name={m.sender_name} className="mb-0.5 mx-1" />

                          {editingId === m.id ? (
                            /* ── Inline editor ── */
                            <div className="w-full min-w-[200px] flex flex-col gap-1.5">
                              <AutoGrowTextarea
                                autoFocus
                                value={editBody}
                                onChange={(e) => setEditBody(e.target.value)}
                                onKeyDown={(e) => handleEditKeyDown(e, m)}
                                rows={2}
                                className="w-full rounded-xl border border-primary/40 bg-card px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20"
                              />
                              <div className="flex items-center gap-2 justify-end">
                                <button
                                  onClick={cancelEdit}
                                  className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleEditSave(m)}
                                  disabled={!editBody.trim() || savingEdit}
                                  className="flex items-center gap-1 text-[11px] font-semibold text-primary-foreground bg-primary hover:bg-primary/90 px-2.5 py-1 rounded-md disabled:opacity-40 transition-colors"
                                >
                                  {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className={`flex items-center gap-1.5 ${mine ? "flex-row-reverse" : "flex-row"}`}>
                              {/* Bubble */}
                              <div
                                className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]
                                  ${mine
                                    ? "bg-primary text-primary-foreground rounded-br-sm shadow-sm"
                                    : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm"
                                  }`}
                              >
                                {m.body}
                              </div>
                              {/* Edit button — only on my own messages */}
                              {mine && (
                                <button
                                  onClick={() => startEdit(m)}
                                  className="shrink-0 h-6 w-6 rounded-full bg-slate-200/70 hover:bg-slate-300 text-slate-600 flex items-center justify-center opacity-50 hover:opacity-100 transition-all"
                                  aria-label="Edit message"
                                  title="Edit message"
                                >
                                  <Pencil className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          )}

                          {/* Time — always visible, full date on hover */}
                          <div className={`flex items-center gap-1 mt-1 ${mine ? "flex-row-reverse" : "flex-row"}`}>
                            <Clock className="h-2.5 w-2.5 text-slate-400 shrink-0" />
                            <span className="text-[10px] text-slate-400">
                              {hoveredId === m.id
                                ? `${formatFullDate(m.created_at)} · ${formatTime(m.created_at)}`
                                : formatTime(m.created_at)
                              }
                            </span>
                            {m.edited_at && (
                              <span className="text-[10px] italic text-slate-400">· edited</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div className="shrink-0 border-t border-slate-200 bg-white px-3 pt-3 pb-3">
            {sendError && (
              <p className="mb-2 rounded-lg px-2.5 py-1.5 text-[11px] font-medium leading-snug"
                style={{ backgroundColor: "rgba(204,51,51,0.12)", color: "#CC3333" }}>
                {sendError}
              </p>
            )}
            <div className="flex items-end gap-2 rounded-xl border border-border bg-muted px-3 py-2 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/40 transition-all">
              <AutoGrowTextarea
                ref={inputRef}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  if (sendError) setSendError(null);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Type a message…"
                rows={1}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 leading-relaxed"
                style={{ minHeight: "22px" }}
              />
              <button
                onClick={handleSend}
                disabled={!body.trim() || sending}
                className="shrink-0 h-8 w-8 rounded-lg bg-gold-gradient text-primary-foreground flex items-center justify-center disabled:opacity-35 hover:brightness-[1.06] transition-all active:scale-95"
              >
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center tracking-wide">
              Enter to send · Shift+Enter for new line
            </p>
          </div>
        </ChatSideDrawer>
      )}

      {/* ── Floating bubble — hidden while the rail is open ─────────── */}
      {!open && (
      <div className="fixed bottom-5 right-5 z-[999]">
      <button
        onClick={() => setOpen(true)}
        className="relative h-14 w-14 rounded-full bg-gold-gradient text-primary-foreground shadow-xl flex items-center justify-center hover:brightness-[1.06] hover:scale-105 transition-all duration-150 active:scale-95"
        aria-label="Toggle advisor chat"
      >
        <MessageCircle className="h-6 w-6" />

        {/* Unread badge */}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md">
            {unread > 99 ? "99+" : unread}
          </span>
        )}

        {/* Pulse ring when unread */}
        {!open && unread > 0 && (
          <span className="absolute inset-0 rounded-full animate-ping bg-primary opacity-20" />
        )}
      </button>
      </div>
      )}
    </>
  );
}
